import type { SupportedLanguage } from "../locales";
import type { DubbingLanguagePair } from "./dubbingLanguagePairs";

export const DUBBING_LANDING_SEO_SLUG = "ai-video-voice-over-dubbing";

export type FaqItem = {
  question: string;
  answer: string;
};

type DubbingLandingContent = {
  heroTitle: string;
  title: string;
  description: string;
  keywords: string;
  faq: FaqItem[];
};

export const dubbingLandingContent: Record<SupportedLanguage, DubbingLandingContent> = {
  en: {
    heroTitle: "AI Video Voice Over Dubbing",
    title: "AI Video Voice Over Dubbing | VEVI Monster",
    description:
      "AI video voice over dubbing for creators and brands. Replace video audio with natural multilingual voice over in minutes.",
    keywords:
      "ai video voice over dubbing, ai dubbing, voice over video, multilingual dubbing, video localization",
    faq: [
      {
        question: "What is AI video voice over dubbing?",
        answer:
          "It replaces your original video audio with an AI-generated voice in another language while keeping timing and natural speech flow.",
      },
      {
        question: "How fast is AI video voice over dubbing?",
        answer:
          "Most videos are processed within minutes. Final speed depends on video duration, source quality, and queue load.",
      },
      {
        question: "Does dubbing keep the original speaker's voice?",
        answer:
          "Yes, you can use voice cloning to preserve the speaker identity and make the translated voice sound close to the original.",
      },
      {
        question: "Which languages and formats are supported?",
        answer:
          "The page supports major European and global languages. Upload standard video formats like MP4 and get a dubbed output ready to publish.",
      },
    ],
  },
  ru: {
    heroTitle: "AI Озвучка и Дубляж Видео",
    title: "AI Озвучка и Дубляж Видео | VEVI Monster",
    description:
      "AI video voice over dubbing для авторов и брендов. Заменяйте звук в видео на естественную многоязычную озвучку за минуты.",
    keywords:
      "ai video voice over dubbing, озвучка видео, ai дубляж, перевод голоса, локализация видео",
    faq: [
      {
        question: "Что такое AI video voice over dubbing?",
        answer:
          "Это замена оригинальной дорожки в видео на AI-озвучку на другом языке с сохранением тайминга и естественной речи.",
      },
      {
        question: "Как быстро работает AI video voice over dubbing?",
        answer:
          "Обычно обработка занимает несколько минут. Точное время зависит от длины видео, качества исходника и текущей очереди.",
      },
      {
        question: "Сохраняется ли голос спикера?",
        answer:
          "Да, можно использовать клонирование голоса, чтобы перевод звучал максимально близко к оригинальному голосу.",
      },
      {
        question: "Какие языки и форматы поддерживаются?",
        answer:
          "Поддерживаются основные европейские и мировые языки, а также популярные видеоформаты, включая MP4.",
      },
    ],
  },
  es: {
    heroTitle: "Doblaje y Voz en Off de Video con IA",
    title: "Doblaje y Voz en Off de Video con IA | VEVI Monster",
    description:
      "AI video voice over dubbing para creadores y marcas. Reemplaza el audio de tu video con voz natural multilingue en minutos.",
    keywords:
      "ai video voice over dubbing, doblaje con ia, voz en off para video, localizacion de video",
    faq: [
      {
        question: "Que es AI video voice over dubbing?",
        answer:
          "Es la sustitucion del audio original por una voz generada con IA en otro idioma, manteniendo ritmo y naturalidad.",
      },
      {
        question: "Que tan rapido es AI video voice over dubbing?",
        answer:
          "La mayoria de los videos se procesan en minutos. El tiempo depende de la duracion del video y la carga de cola.",
      },
      {
        question: "Se conserva la voz original del hablante?",
        answer:
          "Si. Puedes usar clonacion de voz para mantener la identidad del hablante en el idioma traducido.",
      },
      {
        question: "Que idiomas y formatos se admiten?",
        answer:
          "Se admiten los principales idiomas europeos y globales, y formatos de video comunes como MP4.",
      },
    ],
  },
  fr: {
    heroTitle: "Doublage et Voix Off Video par IA",
    title: "Doublage et Voix Off Video par IA | VEVI Monster",
    description:
      "AI video voice over dubbing pour createurs et marques. Remplacez l'audio video par une voix multilingue naturelle en quelques minutes.",
    keywords:
      "ai video voice over dubbing, doublage ia, voix off video, localisation video",
    faq: [
      {
        question: "Qu'est-ce que AI video voice over dubbing ?",
        answer:
          "C'est le remplacement de l'audio d'origine par une voix IA dans une autre langue, avec synchronisation et rendu naturel.",
      },
      {
        question: "Quelle est la vitesse de traitement ?",
        answer:
          "La plupart des videos sont traitees en quelques minutes, selon la duree, la qualite source et la file d'attente.",
      },
      {
        question: "La voix d'origine peut-elle etre conservee ?",
        answer:
          "Oui, le clonage vocal permet de conserver l'identite du locuteur dans la version traduite.",
      },
      {
        question: "Quels formats et langues sont pris en charge ?",
        answer:
          "Les principales langues europeennes et internationales sont disponibles, ainsi que les formats video standards comme MP4.",
      },
    ],
  },
  de: {
    heroTitle: "KI Video Voice-Over und Synchronisation",
    title: "KI Video Voice-Over und Synchronisation | VEVI Monster",
    description:
      "AI video voice over dubbing fur Creator und Marken. Ersetze Video-Audio in Minuten mit naturlichem mehrsprachigem Voice Over.",
    keywords:
      "ai video voice over dubbing, ki synchronisation, voice over video, videolokalisierung",
    faq: [
      {
        question: "Was ist AI video voice over dubbing?",
        answer:
          "Dabei wird die Original-Tonspur durch eine KI-Stimme in einer anderen Sprache ersetzt, mit naturlichem Timing.",
      },
      {
        question: "Wie schnell ist die Verarbeitung?",
        answer:
          "Die meisten Videos werden in wenigen Minuten verarbeitet, abhangig von Lange, Qualitat und Auslastung.",
      },
      {
        question: "Bleibt die Stimme des Sprechers erhalten?",
        answer:
          "Ja, mit Voice Cloning kann die Sprecheridentitat in der Zielsprache beibehalten werden.",
      },
      {
        question: "Welche Sprachen und Formate werden unterstutzt?",
        answer:
          "Unterstutzt werden wichtige europaische und globale Sprachen sowie Standard-Videoformate wie MP4.",
      },
    ],
  },
  it: {
    heroTitle: "Doppiaggio e Voice Over Video con IA",
    title: "Doppiaggio e Voice Over Video con IA | VEVI Monster",
    description:
      "AI video voice over dubbing per creator e brand. Sostituisci l'audio del video con una voce naturale multilingue in pochi minuti.",
    keywords:
      "ai video voice over dubbing, doppiaggio ai, voice over video, localizzazione video",
    faq: [
      {
        question: "Cos'e AI video voice over dubbing?",
        answer:
          "E la sostituzione dell'audio originale con una voce AI in un'altra lingua, mantenendo ritmo e naturalezza.",
      },
      {
        question: "Quanto e veloce l'elaborazione?",
        answer:
          "La maggior parte dei video viene elaborata in pochi minuti, in base a durata, qualita e coda.",
      },
      {
        question: "La voce originale puo essere mantenuta?",
        answer:
          "Si, con il voice cloning puoi mantenere l'identita vocale del parlante nella lingua di destinazione.",
      },
      {
        question: "Quali lingue e formati sono supportati?",
        answer:
          "Sono supportate principali lingue europee e globali e formati video comuni come MP4.",
      },
    ],
  },
  pt: {
    heroTitle: "Dublagem e Voice Over de Video com IA",
    title: "Dublagem e Voice Over de Video com IA | VEVI Monster",
    description:
      "AI video voice over dubbing para criadores e marcas. Substitua o audio do video por voz natural em varios idiomas em minutos.",
    keywords:
      "ai video voice over dubbing, dublagem com ia, voz para video, localizacao de video",
    faq: [
      {
        question: "O que e AI video voice over dubbing?",
        answer:
          "E a substituicao do audio original por uma voz gerada por IA em outro idioma, mantendo ritmo e naturalidade.",
      },
      {
        question: "Qual e a velocidade do processamento?",
        answer:
          "A maioria dos videos e processada em minutos. O tempo depende da duracao, qualidade e fila atual.",
      },
      {
        question: "A voz original do locutor e preservada?",
        answer:
          "Sim. Com clonagem de voz, voce pode manter a identidade vocal no idioma traduzido.",
      },
      {
        question: "Quais idiomas e formatos sao suportados?",
        answer:
          "Suportamos os principais idiomas europeus e globais e formatos comuns de video como MP4.",
      },
    ],
  },
  pl: {
    heroTitle: "AI Dubbing i Voice Over Wideo",
    title: "AI Dubbing i Voice Over Wideo | VEVI Monster",
    description:
      "AI video voice over dubbing dla tworcow i marek. Zamien audio wideo na naturalny wielojezyczny voice over w kilka minut.",
    keywords:
      "ai video voice over dubbing, dubbing ai, voice over do wideo, lokalizacja wideo",
    faq: [
      {
        question: "Czym jest AI video voice over dubbing?",
        answer:
          "To zamiana oryginalnej sciezki audio na glos AI w innym jezyku z zachowaniem tempa i naturalnosci mowy.",
      },
      {
        question: "Jak szybko dziala przetwarzanie?",
        answer:
          "Wiekszosc filmow jest gotowa w kilka minut. Czas zalezy od dlugosci filmu, jakosci i obciazenia kolejki.",
      },
      {
        question: "Czy mozna zachowac glos oryginalnego mowcy?",
        answer:
          "Tak, klonowanie glosu pozwala zachowac tozsamosc mowcy w przetlumaczonej wersji.",
      },
      {
        question: "Jakie jezyki i formaty sa obslugiwane?",
        answer:
          "Obslugiwane sa najwazniejsze jezyki europejskie i globalne oraz standardowe formaty wideo, np. MP4.",
      },
    ],
  },
  ja: {
    heroTitle: "AI動画ボイスオーバー・吹き替え",
    title: "AI動画ボイスオーバー・吹き替え | VEVI Monster",
    description:
      "AI video voice over dubbing を使って、動画音声を多言語の自然なボイスオーバーに数分で置き換えます。",
    keywords:
      "ai video voice over dubbing, AI吹き替え, 動画ボイスオーバー, 動画ローカライズ",
    faq: [
      {
        question: "AI video voice over dubbing とは何ですか？",
        answer:
          "元の動画音声を、別言語のAI音声に置き換える機能です。タイミングと自然さを維持します。",
      },
      {
        question: "処理時間はどれくらいですか？",
        answer:
          "多くの動画は数分で処理されます。動画尺、音質、キュー状況により変動します。",
      },
      {
        question: "話者の声質は維持できますか？",
        answer:
          "はい。音声クローンを使うと、翻訳後も元の話者に近い声で出力できます。",
      },
      {
        question: "対応言語と形式は？",
        answer:
          "主要な欧州言語とグローバル言語に対応し、MP4 など一般的な動画形式を利用できます。",
      },
    ],
  },
  ko: {
    heroTitle: "AI 비디오 보이스오버 및 더빙",
    title: "AI 비디오 보이스오버 및 더빙 | VEVI Monster",
    description:
      "AI video voice over dubbing으로 영상 오디오를 자연스러운 다국어 보이스오버로 몇 분 안에 교체하세요.",
    keywords:
      "ai video voice over dubbing, AI 더빙, 영상 보이스오버, 영상 현지화",
    faq: [
      {
        question: "AI video voice over dubbing이란 무엇인가요?",
        answer:
          "원본 영상 오디오를 다른 언어의 AI 음성으로 교체하는 기능이며, 타이밍과 자연스러운 말투를 유지합니다.",
      },
      {
        question: "처리 속도는 얼마나 빠른가요?",
        answer:
          "대부분의 영상은 몇 분 내 처리됩니다. 영상 길이, 원본 품질, 대기열 상황에 따라 달라질 수 있습니다.",
      },
      {
        question: "원래 화자의 목소리를 유지할 수 있나요?",
        answer:
          "네, 보이스 클로닝을 사용하면 번역된 언어에서도 원래 화자에 가까운 톤을 유지할 수 있습니다.",
      },
      {
        question: "지원 언어와 포맷은 무엇인가요?",
        answer:
          "주요 유럽 및 글로벌 언어를 지원하며, MP4 같은 일반적인 영상 포맷을 업로드할 수 있습니다.",
      },
    ],
  },
  zh: {
    heroTitle: "AI 视频旁白与配音",
    title: "AI 视频旁白与配音 | VEVI Monster",
    description:
      "使用 AI video voice over dubbing，在几分钟内将视频音频替换为自然的多语言配音。",
    keywords:
      "ai video voice over dubbing, AI配音, 视频旁白, 视频本地化",
    faq: [
      {
        question: "什么是 AI video voice over dubbing？",
        answer:
          "它可以将原视频音轨替换为另一种语言的 AI 配音，同时保持节奏与自然表达。",
      },
      {
        question: "处理速度有多快？",
        answer:
          "大多数视频可在几分钟内完成。具体时间取决于视频时长、源质量和当前队列负载。",
      },
      {
        question: "可以保留原说话人的声音吗？",
        answer:
          "可以。通过语音克隆，可在目标语言中尽量保留原说话人的声音特征。",
      },
      {
        question: "支持哪些语言和格式？",
        answer:
          "支持主要欧洲与全球语言，并支持 MP4 等常见视频格式。",
      },
    ],
  },
};

export function getDubbingLandingFaq(language: SupportedLanguage): FaqItem[] {
  return dubbingLandingContent[language]?.faq || dubbingLandingContent.en.faq;
}

export function getDubbingLandingSeo(language: SupportedLanguage) {
  return dubbingLandingContent[language] || dubbingLandingContent.en;
}

export function getDubbingPairLandingContent(
  language: SupportedLanguage,
  pair: DubbingLanguagePair
) {
  const base = getDubbingLandingSeo(language);
  const pairPhrase = `English to ${pair.target}`;

  if (language === "es" && pair.slug === "spanish") {
    return {
      ...base,
      heroTitle: "Doblaje. Traduce videos con voces de Ingles a Espanol",
      title: "Doblaje. Traduce vídeos con voces de Ingles a Españole | Vevi monsterr",
      description:
        "Doblaje con IA para traducir videos con voces de Ingles a Espanol. Convierte el audio de tus videos en minutos con voces naturales.",
      keywords: `${base.keywords}, doblaje ingles a espanol, traducir video ingles a espanol, doblaje ia espanol`,
      faq: base.faq.map((item) => ({
        ...item,
        question: `${item.question} (Ingles a Espanol)`,
      })),
    };
  }

  return {
    ...base,
    heroTitle: `${base.heroTitle}: ${pairPhrase}`,
    title: `${base.heroTitle}: ${pairPhrase} | VEVI Monster`,
    description: `${base.description} Optimized for ${pairPhrase} AI video voice over dubbing and localization.`,
    keywords: `${base.keywords}, english to ${pair.target.toLowerCase()} dubbing, english to ${pair.target.toLowerCase()} voice over`,
    faq: base.faq.map((item, index) => {
      const suffix = ` (${pairPhrase})`;
      if (index === 0) {
        return {
          question: `${item.question}${suffix}`,
          answer: `${item.answer} This page is focused on ${pairPhrase} video dubbing.`,
        };
      }
      if (index === 1) {
        return {
          question: `${item.question}${suffix}`,
          answer: item.answer,
        };
      }
      if (index === 2) {
        return {
          question: `${item.question}${suffix}`,
          answer: item.answer,
        };
      }
      return {
        question: `${item.question}${suffix}`,
        answer: item.answer,
      };
    }),
  };
}
